import { useEffect, useState, useCallback, useRef } from "react";
import SearchInput from "./SearchInput";

interface Props {
  defaultData: any[] | undefined;
  searchByProperty: string;
  setData: (data: any[]) => void;
  debounceTime?: number;
  resetSearch?: number;
  setResetSearch?: (resetSearch: number) => void;
}

const LocalSearch = ({
  defaultData,
  searchByProperty,
  setData,
  debounceTime = 300,
  resetSearch,
  setResetSearch,
}: Props) => {
  const [search, setSearch] = useState("");
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const defaultDataRef = useRef(defaultData);

  useEffect(() => {
    defaultDataRef.current = defaultData;
  }, [defaultData]);

  const handleSearch = useCallback(
    (v: string) => {
      setSearch(v);

      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      timerRef.current = setTimeout(() => {
        const currentDefaultData = defaultDataRef.current;
        if (!currentDefaultData) return;
        if (v.trim().length > 0) {
          const filteredData = currentDefaultData.filter((item: any) =>
            item[searchByProperty]
              ?.toString()
              .toLowerCase()
              .includes(v.toLowerCase()),
          );
          setData(filteredData);
        } else {
          setData(currentDefaultData);
        }
      }, debounceTime);
    },
    [searchByProperty, setData, debounceTime],
  );

  useEffect(() => {
    if (resetSearch) {
      setSearch("");
      setData(defaultDataRef.current ?? []);
      setResetSearch?.(0);
    }
  }, [resetSearch, setResetSearch, setData]);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  return <SearchInput value={search} onChange={handleSearch} />;
};

export default LocalSearch;
